import * as React from 'react';
import { IRestriction } from '../../models';
import { useData } from '../../core/context/ProvidersContext';
import { StatusBadge } from '../shared/StatusBadge';
import styles from './RestrictionsTable.module.scss';

export interface IRestrictionsTableProps {
  restrictions: IRestriction[];
  onDataChanged: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export const RestrictionsTable: React.FC<IRestrictionsTableProps> = ({
  restrictions, onDataChanged,
}) => {
  const data = useData();
  const [editingCell, setEditingCell] = React.useState<{ id: number; field: string } | null>(null);
  const [editValue, setEditValue] = React.useState('');

  const depotGroups = React.useMemo(() => {
    const groups: Record<string, IRestriction[]> = {};
    restrictions.forEach(r => {
      const depot = r.DepotTitle || 'Sans dépôt';
      if (!groups[depot]) groups[depot] = [];
      groups[depot].push(r);
    });
    return groups;
  }, [restrictions]);

  const handleStartEdit = (id: number, field: string, currentValue: string): void => {
    setEditingCell({ id, field });
    setEditValue(currentValue);
  };

  const handleSave = async (): Promise<void> => {
    if (!editingCell) return;
    const updates: Partial<IRestriction> = {};
    (updates as Record<string, string>)[editingCell.field] = editValue;
    await data.updateRestriction(editingCell.id, updates);
    setEditingCell(null);
    onDataChanged();
  };

  const renderCell = (
    restriction: IRestriction,
    field: keyof IRestriction,
    displayValue: string,
    className?: string
  ): React.ReactElement => {
    const isEditing = editingCell?.id === restriction.Id && editingCell?.field === field;

    if (isEditing) {
      return (
        <td className={className}>
          <input
            className={styles.editInput}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditingCell(null); }}
            autoFocus
          />
        </td>
      );
    }

    return (
      <td
        className={`${className || ''} ${styles.editableCell}`}
        onClick={() => handleStartEdit(restriction.Id, field, String(restriction[field] || ''))}
      >
        {displayValue || '\u00A0'}
      </td>
    );
  };

  return (
    <div className={styles.wrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Dépôt</th>
            <th>Produit</th>
            <th>Restriction estimée</th>
            <th>Date début</th>
            <th>Date fin</th>
            <th>Remarques</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(depotGroups).map(([depotName, items]) => (
            <React.Fragment key={depotName}>
              <tr className={styles.depotGroupRow}>
                <td colSpan={6}>{depotName}</td>
              </tr>
              {items.map((r, rowIdx) => (
                <tr key={r.Id} className={styles.dataRow} style={{ animationDelay: `${rowIdx * 0.04}s` }}>
                  <td>{r.DepotTitle}</td>
                  <td><StatusBadge contenu={r.Produit} compact /></td>
                  {renderCell(r, 'RestrictionEstimee',
                    r.RestrictionEstimee || '',
                    r.RestrictionEstimee ? styles.restrictionValue : styles.noRestriction
                  )}
                  {renderCell(r, 'DateDebut', formatDate(r.DateDebut), styles.dateCell)}
                  {renderCell(r, 'DateFin', formatDate(r.DateFin), styles.dateCell)}
                  {renderCell(r, 'Remarques', r.Remarques || '', styles.remarkCell)}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};
